import { isEqual } from "lodash";

import { generateColumnIdForField } from "../explore/exploreFieldUtils.js";
import {
  ExploreDetailField,
  ExploreFieldType,
  ExploreSpec,
} from "../explore/types.js";
import { HexSLTypes } from "../index.js";

import {
  HqlAggregationBuilder,
  HqlSelectionBuilder,
} from "./HqlNextSpecBuilder.js";
import { getDatasetPathForField } from "./hqlNextUtils.js";

export function addDetailFieldsToHqlBuilder({
  builder,
  fields,
  spec,
}: {
  builder: HqlSelectionBuilder | HqlAggregationBuilder;
  spec: ExploreSpec;
  fields: ExploreDetailField[];
}): void {
  function addField({
    selectItem,
  }: {
    selectItem: HexSLTypes.SelectItem;
    dimension?: HexSLTypes.Dimension;
  }): void {
    if (builder instanceof HqlAggregationBuilder) {
      builder.detail(selectItem);
    } else {
      builder.select(selectItem);
    }
  }
  if (fields.length === 0) {
    return;
  }

  fields.forEach((field) => {
    // Detail fields are row-level drill-through columns that must resolve to a
    // dimension/column expression on the underlying dataset. A measure can
    // never be a valid detail field: on a cross-model semantic view it emits
    // as a bare `expr` on the related dataset and hex-sl looks it up in that
    // dataset's schema — where measures don't live — producing a
    // "Column {name} not found in schema" error. Skip these unconditionally,
    // so a stale/agent-authored `details.fields` entry with fieldType
    // MEASURE can't blow up an otherwise-valid query.
    if (field.fieldType === ExploreFieldType.MEASURE) {
      return;
    }
    // if a field is already selected in the group by of the transform, we
    // don't need to add it again as a detail field
    if (
      spec.fields.find(
        (f) =>
          f.value === field.value &&
          f.truncUnit == null &&
          f.aggregation == null &&
          isEqual(f.queryPath, field.queryPath),
      )
    ) {
      return;
    }

    let dimension;

    if (field.queryPath.length === 0) {
      addField({
        selectItem: {
          dimension: field.value,
          dataset: getDatasetPathForField(field),
        },
        dimension,
      });
    } else {
      const alias = generateColumnIdForField(field);
      addField({
        selectItem: {
          expr: { name: field.value },
          alias,
          dataset: getDatasetPathForField(field),
        },
        dimension,
      });
    }
  });
}
